/**
 * @file Settings.tsx
 * @module GlobalSettings
 * @description Karšu pārlūka globālo iestatījumu nodrošinātājs. Ielādē slāņu
 *              konfigurāciju no ĢIS datu servisa API un nodrošina React kontekstu
 *              visiem kartes komponentiem. Pārvalda iepriekš definētas slāņu kopas
 *              tiešo saišu skatiem (neplānotie notikumi, plānotie notikumi, UVIS,
 *              kameras, VMS). Saņem datus no Kartes vizualizācijas pārvaldības.
 */

import { createContext, useEffect, useState } from "react";
import MapWrapper from "./map/Map";
import axios from "axios";
import { ENTRYPOINT } from "./map/Endpoints";
import { DataResponse, GroupEntity, LayerResponse, LayerSetResponse, RemoteResponse, setLayerEntities } from "./store/Layers.repository";
import { Cookies } from "./interceptors/Cookies";
import { LangHeader } from "./interceptors/Lang-header";
import { ClassifierComponent } from "./classifiers/Classifiers";
import { isMobile } from "react-device-detect";
import { TitleComponent } from "./settings/Title";
import { updateSettingStore } from "./settings/Settings.repository";
import { useSearchParams } from "react-router-dom";
import { DEFAULT_LAYERSET, LAYER_SET$ } from "./map/layer-set/Layerset-wrapper";
import { overviewActive$ } from "./map/bubble/Bubble-wrapper";

/** Default values for global settings context before server config is loaded */
const defaultContextValues: GlobalSettingsContextInterface = {
	refresh_rate: 500,
	system_code: "NPP",
	use_overview: true,
	predefined_layers: undefined,
};

/** React context providing global settings (refresh rate, system code, layer presets) to the component tree */
export const GlobalSettingsContext = createContext(defaultContextValues);

/**
 * Predefined layer presets for direct-link map views.
 *
 * Each preset defines which layer groups and individual layers are automatically
 * activated when the corresponding view is opened (e.g., via URL parameter).
 *
 * - mainGroup: primary layer group ID in the navigation panel (from backend config)
 * - features: array of Kafka IDs to filter specific features (empty = show all)
 * - groups[].groupId: layer group ID from backend configuration
 * - groups[].layerId: specific layer IDs within the group to activate
 */
const PREDEFINED_LAYER_PRESETS = {
	/** Unplanned/unexpected traffic events (accidents, road closures, ice, floods) */
	unexpectedEvent: {
		mainGroup: 5,       // Unplanned events group
		features: [] as string[],
		groups: [
			{ groupId: 5, layerId: [10, 11, 12, 6, 13, 14] },  // Road events, ice, floods, etc.
			{ groupId: 6, layerId: [15, 8] },                   // VMS signs and cameras
			{ groupId: 13, layerId: [45] },                      // UVIS warnings
		],
	},
	/** Planned events (scheduled roadworks, construction, maintenance) */
	plannedEvent: {
		mainGroup: 4,       // Planned events group
		features: [] as string[],
		groups: [{ groupId: 4, layerId: [16, 17, 18, 19, 20, 21, 22, 23, 24] }],  // All planned event sub-layers
	},
	/** UVIS road condition information (winter/summer road conditions) */
	UVIS: {
		mainGroup: 14,      // UVIS group
		features: [] as string[],
		groups: [{ groupId: 14, layerId: [58, 61] }],  // UVIS message layers
	},
	/** Traffic cameras */
	camera: {
		mainGroup: 6,       // Devices group
		features: [],
		groups: [{ groupId: 6, layerId: [8] }],  // Camera layer
	},
	/** Variable Message Signs */
	VMS: {
		mainGroup: 6,       // Devices group
		features: [],
		groups: [{ groupId: 6, layerId: [15] }],  // VMS layer
	},
};

/** Props for the GlobalSettings component */
interface Props {
	/** Preset name key (e.g., "unexpectedEvent", "camera") for direct-link views */
	name?: string;
	/** Array of Kafka IDs to filter specific features within the preset */
	ids?: string[];
}

/** Shape of the global settings context provided to all child components */
interface GlobalSettingsContextInterface {
	/** Map data refresh interval in seconds (multiplied by 1000 for WebSocket interval) */
	refresh_rate: number;
	/** System code: "NPP" for public portal, "SIPR" for internal system */
	system_code: "NPP" | "SIPR";
	/** Whether to show the overview (bubble) mini-map */
	use_overview?: boolean;
	/** Active predefined layer preset configuration (if navigated via direct link) */
	predefined_layers?: typeof PREDEFINED_LAYER_PRESETS.unexpectedEvent;
}

/**
 * Global settings provider component.
 *
 * Fetches system configuration (refresh rate, system code) and layer definitions
 * from the backend API. Parses URL search parameters to restore UI settings
 * (opacity, visibility toggles). Refreshes layer data every 30 seconds.
 *
 * Renders the map only after layers are fully loaded.
 *
 * @param {Props} layerData - Optional preset name and feature IDs for direct-link views
 * @returns {React.ReactElement} Provider wrapping interceptors, classifiers, and the map
 */
export const GlobalSettings = (layerData: Props) => {
	const [context, setContext] = useState<GlobalSettingsContextInterface>({
		...defaultContextValues,
		predefined_layers:
			layerData.name && layerData.ids
				? {
						...PREDEFINED_LAYER_PRESETS[layerData.name as keyof typeof PREDEFINED_LAYER_PRESETS],
						features: layerData.ids,
				  }
				: undefined,
	});
	const [loaded, setLoaded] = useState(false);
	const [searchParams] = useSearchParams();

	useEffect(() => {
		const paramsObject = Object.fromEntries(searchParams.entries());
		if (paramsObject.layers === undefined) {
			overviewActive$.next(true);
		}
		if (searchParams.get("baseOpacity")) {
			updateSettingStore({
				baseOpacity: Number(searchParams.get("baseOpacity")) ?? 100,
				showAttributionBtn: searchParams.get("showAttributionBtn") === "true",
				showBaseOpacity: searchParams.get("showBaseOpacity") === "true",
				showLegendBtn: searchParams.get("showLegendBtn") === "true",
				showObjectOpacity: searchParams.get("showObjectOpacity") === "true",
				showPanBtn: searchParams.get("showPanBtn") === "true",
				showLayerSets: searchParams.get("showLayerSets") === "true",
			});
		}
	}, []);

	useEffect(() => {
		axios
			.get<{
				refresh_rate: number;
				system_code: "NPP" | "SIPR";
				use_overview: boolean;
			}>(ENTRYPOINT + "/settings/")
			.then((res) => {
				setContext({ ...context, ...res.data });
			});
		const updateLayers = () => {
			axios
				.get<{
					base: LayerResponse[];
					data: RemoteResponse[];
					remote: RemoteResponse[];
					layer_sets: LayerSetResponse[];
				}>(ENTRYPOINT + "/layer/layers")
				.then((res) => {
					const entities: GroupEntity[] = Object.keys(res.data)
						.map((key) => {
							if (key === "layer_sets") {
								const layersets = res.data[key];
								const enabledLs = layersets.find((ls) => ls.enabled);
								if (enabledLs) {
									DEFAULT_LAYERSET.data_layers = enabledLs.data_layers;
									DEFAULT_LAYERSET.name = enabledLs.name;
									DEFAULT_LAYERSET.id = enabledLs.id;
								}
								LAYER_SET$.next(layersets);
								return [];
							}

							if (key === "base") {
								return {
									name: "BaseLayers",
									group: key,
									id: key + "-" + 0,
									layers: res.data[key].map((l) => ({ ...l, group: key })),
								} as any;
							} else {
								return (res.data[key as keyof typeof res.data] as RemoteResponse[]).map(
									(d) =>
										({
											attributions: d.attributions,
											name: d.name,
											id: key + "-" + d.id,
											group: key,
											dataSource: d.url_to_datasource,
											use_uvis_popups: d.use_uvis_popups,
											layers: (d as RemoteResponse | DataResponse).layers.map((l) => ({
												...l,
												group: key,
												groupName: d.name,
												merged_tiles: (l as any).merged_tiles,
												zoom_from: isMobile ? (l as any).zoom_from - 2 : (l as any).zoom_from,
											})),
										} as GroupEntity)
								);
							}
						})
						.flat();
					setLayerEntities(entities);
					setLoaded(true);
				});
		};

		updateLayers();
		const interval = setInterval(() => {
			updateLayers();
		}, 30000);

		return () => {
			clearInterval(interval);
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<GlobalSettingsContext.Provider value={context}>
			<Cookies></Cookies>
			<LangHeader></LangHeader>
			<ClassifierComponent></ClassifierComponent>
			{context.system_code === "NPP" && <TitleComponent></TitleComponent>}
			{loaded && <MapWrapper></MapWrapper>}
		</GlobalSettingsContext.Provider>
	);
};
